import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/ApiClient";
import {
  Upload,
  Loader2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Transaction {
  date: string;
  description: string;
  amount: number;
  category: string;
  type: "income" | "expense";
}

interface Asset {
  id: number;
  name: string;
  address: string;
  slug: string;
  asset_type: string;
}

interface Property {
  propertyIdentifier: string;
  propertyAddress: string | null;
  beginningCashBalance: number;
  endingCashBalance: number;
  summary: {
    totalIncome: number;
    totalExpenses: number;
    netOperatingIncome: number;
    netIncome: number;
  };
  transactions: Transaction[];
}

interface StatementAnalysis {
  propertyAddress: string | null;
  propertyManagementType: string;
  statementPeriod: string;
  summary: {
    totalIncome: number;
    totalExpenses: number;
    netOperatingIncome: number;
    netIncome: number;
    beginningCashBalance: number;
    endingCashBalance: number;
  };
  roiAnalysis: {
    explanation: string;
    estimatedAnnualizedROI: number | null;
  };
  transactions: Transaction[];
  properties?: Property[]; // Multiple properties if present
}

export default function StatementUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [pmType, setPmType] = useState<string>("AppFolio");
  const [loading, setLoading] = useState(false);

  // Determine accepted file types based on PM type
  const getAcceptedFileTypes = () => {
    if (pmType === "Rent Manager") {
      return ".xls,.xlsx";
    }
    // PropertyWare and other PM types use PDF
    return ".pdf";
  };
  const [analysis, setAnalysis] = useState<StatementAnalysis | null>(null);
  const [matchedAssets, setMatchedAssets] = useState<Asset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [propertyAssetMap, setPropertyAssetMap] = useState<
    Record<string, number | null>
  >({});
  const [propertyMatchedAssets, setPropertyMatchedAssets] = useState<
    Record<string, Asset[]>
  >({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pmType", pmType);

    try {
      const response = await apiClient.post("/statements/analyze", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const analysisData = response.data;
      setAnalysis(analysisData);

      // Handle multiple properties
      if (analysisData.properties && analysisData.properties.length > 1) {
        toast.success(
          `Statement analyzed: Found ${analysisData.properties.length} properties`
        );

        // Search for matching assets for each property
        const assetMap: Record<string, number | null> = {};
        const matchedAssetsMap: Record<string, Asset[]> = {};

        for (const prop of analysisData.properties) {
          const identifier = prop.propertyIdentifier;
          assetMap[identifier] = null;
          matchedAssetsMap[identifier] = [];

          if (prop.propertyAddress || prop.propertyIdentifier) {
            try {
              const searchTerm =
                prop.propertyAddress || prop.propertyIdentifier;
              const assetsResponse = await apiClient.get(
                "/statements/find-asset",
                {
                  params: { address: searchTerm },
                }
              );

              const assets = assetsResponse.data.assets || [];
              matchedAssetsMap[identifier] = assets;

              if (assets.length === 1) {
                assetMap[identifier] = assets[0].id;
              }
            } catch (error) {
              console.error(
                "Error finding assets for property:",
                identifier,
                error
              );
            }
          }
        }
        setPropertyAssetMap(assetMap);
        setPropertyMatchedAssets(matchedAssetsMap);
      } else {
        toast.success("Statement analyzed successfully");

        // If we have a property address, search for matching assets
        if (analysisData.propertyAddress) {
          try {
            const assetsResponse = await apiClient.get(
              "/statements/find-asset",
              {
                params: { address: analysisData.propertyAddress },
              }
            );

            const assets = assetsResponse.data.assets || [];
            setMatchedAssets(assets);

            // Auto-select if there's exactly one match
            if (assets.length === 1) {
              setSelectedAssetId(assets[0].id);
              toast.info(`Matched to property: ${assets[0].name}`);
            } else if (assets.length > 1) {
              toast.info(`Found ${assets.length} matching properties`);
            }
          } catch (error) {
            console.error("Error finding assets:", error);
            // Don't show error toast, just continue without matching
          }
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to analyze statement");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!analysis || !file) return;

    setImporting(true);
    try {
      // Handle multiple properties
      if (analysis.properties && analysis.properties.length > 1) {
        const properties = analysis.properties.map((prop) => ({
          propertyIdentifier: prop.propertyIdentifier,
          propertyAddress: prop.propertyAddress,
          transactions: prop.transactions,
          assetId: propertyAssetMap[prop.propertyIdentifier] || null,
          createAsset: !propertyAssetMap[prop.propertyIdentifier],
        }));

        const response = await apiClient.post("/statements/import-multiple", {
          properties,
        });
        const { results, successfulImports, totalProperties } = response.data;

        toast.success(
          `Successfully imported ${successfulImports} of ${totalProperties} properties`
        );

        // Navigate to first successfully imported property
        const firstSuccess = results.find((r: any) => r.success);
        if (firstSuccess?.assetSlug) {
          navigate(`/app/assets/${firstSuccess.assetSlug}`);
        }
      } else {
        // Single property import (existing logic)
        const payload = {
          assetId: selectedAssetId,
          transactions: analysis.transactions,
          propertyAddress: analysis.propertyAddress,
          propertyIdentifier: analysis.propertyAddress, // Use address as identifier if available
          createAsset: !selectedAssetId && !!analysis.propertyAddress,
        };

        const response = await apiClient.post("/statements/import", payload);
        const { assetSlug, transactionsImported } = response.data;

        toast.success(
          `Successfully imported ${transactionsImported} transactions`
        );

        // Upload the statement file to the asset as an attachment
        if (file && assetSlug) {
          try {
            const fileFormData = new FormData();
            fileFormData.append("file", file);
            fileFormData.append(
              "tags",
              JSON.stringify(["statement", pmType.toLowerCase()])
            );

            await apiClient.post(
              `/assets/${assetSlug}/attachments`,
              fileFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            toast.success("Statement file uploaded to property");
          } catch (error) {
            console.error("Error uploading statement file:", error);
            // Don't fail the whole import if file upload fails
            toast.error(
              "Transactions imported but failed to upload statement file"
            );
          }
        }

        // Navigate to the asset page
        if (assetSlug) {
          navigate(`/app/assets/${assetSlug}`);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to import transactions");
    } finally {
      setImporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Statement Analysis
        </h1>
        <p className="text-muted-foreground">
          Upload your property management statement to extract financial data
          and insights.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload Statement</CardTitle>
          <CardDescription>
            Select the property management software and upload your statement
            file (PDF or XLS).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <label
              htmlFor="pm-type"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Property Management Software
            </label>
            <Select
              value={pmType}
              onValueChange={(value) => {
                setPmType(value);
                setFile(null); // Reset file when PM type changes
              }}
            >
              <SelectTrigger id="pm-type">
                <SelectValue placeholder="Select software" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AppFolio">AppFolio</SelectItem>
                <SelectItem value="Buildium">Buildium</SelectItem>
                <SelectItem value="Yardi">Yardi</SelectItem>
                <SelectItem value="Rent Manager">Rent Manager</SelectItem>
                <SelectItem value="PropertyWare">PropertyWare</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <label
              htmlFor="file"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Statement File{" "}
              {pmType === "Rent Manager" ? "(XLS/XLSX)" : "(PDF)"}
            </label>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                className="w-full relative"
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                {file ? file.name : "Choose File"}
                <input
                  id="file-upload"
                  type="file"
                  accept={getAcceptedFileTypes()}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </Button>
              <Button onClick={handleUpload} disabled={!file || loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Analyze
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {analysis && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Income
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(analysis.summary.totalIncome)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Expenses
                </CardTitle>
                <TrendingDown className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(analysis.summary.totalExpenses)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Net Operating Income
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(analysis.summary.netOperatingIncome)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Est. Annualized ROI
                </CardTitle>
                <PieChart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analysis.roiAnalysis.estimatedAnnualizedROI
                    ? `${(analysis.roiAnalysis.estimatedAnnualizedROI * 100).toFixed(2)}%`
                    : "N/A"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analysis.roiAnalysis.explanation.substring(0, 100)}...
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Property Selection and Import */}
          {analysis.properties && analysis.properties.length > 1 ? (
            <Card>
              <CardHeader>
                <CardTitle>Import Multiple Properties</CardTitle>
                <CardDescription>
                  Found {analysis.properties.length} properties in this
                  statement. Select an existing property for each or create new
                  ones.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {analysis.properties.map((prop, idx) => {
                  const propAssets =
                    propertyMatchedAssets[prop.propertyIdentifier] || [];
                  const selectedId =
                    propertyAssetMap[prop.propertyIdentifier] || null;

                  return (
                    <div
                      key={prop.propertyIdentifier}
                      className="border rounded-lg p-4 space-y-3"
                    >
                      <div className="font-medium">
                        {prop.propertyIdentifier}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {prop.transactions.length} transactions • Income:{" "}
                        {formatCurrency(prop.summary.totalIncome)} • Expenses:{" "}
                        {formatCurrency(prop.summary.totalExpenses)}
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium">
                          Select Property
                        </div>
                        <Select
                          value={selectedId?.toString() || "new"}
                          onValueChange={(value) => {
                            setPropertyAssetMap((prev) => ({
                              ...prev,
                              [prop.propertyIdentifier]:
                                value === "new" ? null : Number(value),
                            }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose existing or create new" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">
                              Create New Property
                            </SelectItem>
                            {propAssets.map((asset) => (
                              <SelectItem
                                key={asset.id}
                                value={asset.id.toString()}
                              >
                                {asset.name} - {asset.address}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {!selectedId && (
                        <div className="text-xs text-muted-foreground">
                          A new property will be created:{" "}
                          <strong>
                            {prop.propertyAddress || prop.propertyIdentifier}
                          </strong>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handleImport}
                    disabled={importing}
                    className="flex-1"
                  >
                    {importing && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Import All Properties
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  This will import transactions for all{" "}
                  {analysis.properties.length} properties. New properties will
                  be created automatically if not matched.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Import to Property</CardTitle>
                <CardDescription>
                  {analysis.propertyAddress
                    ? `Detected address: ${analysis.propertyAddress}`
                    : "No property address detected in statement"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {matchedAssets.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Select Property</div>
                    <Select
                      value={
                        selectedAssetId ? selectedAssetId.toString() : undefined
                      }
                      onValueChange={(value) => {
                        const numValue = Number(value);
                        setSelectedAssetId(
                          Number.isNaN(numValue) ? null : numValue
                        );
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a property" />
                      </SelectTrigger>
                      <SelectContent>
                        {matchedAssets.map((asset) => (
                          <SelectItem
                            key={asset.id}
                            value={asset.id.toString()}
                          >
                            {asset.name} - {asset.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {matchedAssets.length === 0 && analysis.propertyAddress && (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    No existing properties found. A new property will be created
                    at: <strong>{analysis.propertyAddress}</strong>
                  </div>
                )}

                {!analysis.propertyAddress && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                    ⚠️ No property address detected. Please select an existing
                    property or the import will fail.
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={handleImport}
                    disabled={
                      importing ||
                      (!selectedAssetId && !analysis.propertyAddress)
                    }
                    className="flex-1"
                  >
                    {importing && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {selectedAssetId
                      ? "Import to Selected Property"
                      : analysis.propertyAddress
                        ? "Create Property & Import"
                        : "Import Transactions"}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  This will import {analysis.transactions.length} transactions
                  to the selected or new property.
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Transactions</CardTitle>
              <CardDescription>
                Extracted transactions from the statement period:{" "}
                {analysis.statementPeriod}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analysis.transactions.map((transaction, index) => (
                    <TableRow key={index}>
                      <TableCell>{transaction.date}</TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>{transaction.category}</TableCell>
                      <TableCell
                        className={`text-right ${transaction.type === "income" ? "text-green-600" : "text-red-600"}`}
                      >
                        {transaction.type === "expense" ? "-" : ""}
                        {formatCurrency(transaction.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
